import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, ChevronDown, Eye, EyeOff, Menu, ShieldCheck, Flag } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";
import Wordmark from "./Wordmark";

export const DEFAULT_CITY = "london";

export function useLocations() {
  return useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/public/locations")).data, staleTime: 600000 });
}

/** The city the visitor is currently looking at (from the URL), falling back to the main city. */
export function useCurrentCity(cities = []) {
  const { pathname } = useLocation();
  const first = pathname.split("/")[1];
  return cities.find((c) => c.slug === first) || cities.find((c) => c.is_default) || cities[0] || { slug: DEFAULT_CITY, name: "London", areas: [] };
}

const navCls = ({ isActive }) =>
  `rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "text-lux-silverBright" : "text-lux-text/80 hover:text-lux-silverBright"}`;

export default function PublicHeader() {
  const { data } = useLocations();
  const cities = data?.cities || [];
  const city = useCurrentCity(cities);
  const areas = city?.areas || [];
  const { saved, blur, setBlur } = useVisitor();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-lux-border bg-[#0B0B0C]/85 backdrop-blur supports-[backdrop-filter]:bg-[#0B0B0C]/70">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-lux-silver focus:px-3 focus:py-2 focus:text-black">
        Skip to content
      </a>
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Wordmark size="text-[26px]" glow />
        <nav className="hidden items-center gap-0.5 md:flex" aria-label="Main">
          {(cities.length ? cities : [{ slug: "london", name: "London" }, { slug: "birmingham", name: "Birmingham" }, { slug: "manchester", name: "Manchester" }]).map((c) => (
            <NavLink key={c.slug} to={`/${c.slug}`} end className={navCls} data-testid={`public-header-city-link-${c.slug}`}>{c.name}</NavLink>
          ))}
          <span className="mx-1 h-4 w-px bg-lux-border" aria-hidden="true" />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 rounded-md px-3 py-2 text-sm text-lux-text/80 hover:text-lux-silverBright" data-testid="public-header-areas-menu">
              {city?.name} areas <ChevronDown className="h-3.5 w-3.5 text-lux-silver" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="min-w-[200px] border-lux-border bg-lux-surface" align="start">
              {areas.map((a) => (
                <DropdownMenuItem key={a.slug} asChild>
                  <Link to={`/${city.slug}/${a.slug}`} className="cursor-pointer" data-testid={`public-header-area-${a.slug}`}>{a.name}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to={`/${city?.slug || DEFAULT_CITY}/browse`} className={navCls} data-testid="public-header-browse-link">Browse</NavLink>
          <NavLink to="/safety" className={navCls} data-testid="public-header-safety-link">Safety</NavLink>
          <NavLink to="/help" className={navCls} data-testid="public-header-help-link">Help</NavLink>
        </nav>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setBlur(!blur)} aria-pressed={blur} className={`hidden h-10 w-10 items-center justify-center rounded-md md:inline-flex ${blur ? "text-lux-silverBright" : "text-lux-silver hover:bg-white/5 hover:text-lux-silverBright"}`}
            aria-label={blur ? "Photos are blurred. Show photos" : "Blur photos while browsing"} title={blur ? "Show photos" : "Blur photos"} data-testid="public-header-blur-toggle">
            {blur ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
          </button>
          <Link to="/saved" className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-lux-silver hover:bg-white/5 hover:text-lux-silverBright" aria-label={`Saved profiles (${saved.length})`} data-testid="public-header-saved-link">
            <Bookmark className="h-[18px] w-[18px]" />
            {saved.length > 0 && <span className="absolute right-1 top-1 min-w-[16px] rounded-full bg-lux-silver px-1 text-center text-[10px] font-semibold leading-4 text-black" data-testid="public-header-saved-count">{saved.length}</span>}
          </Link>
          <Link to="/advertiser/login" className="lux-btn lux-btn-secondary lux-btn-sm hidden sm:inline-flex" data-testid="public-header-advertiser-login-link">
            For advertisers
          </Link>
          <button onClick={() => setOpen(true)} className="rounded-md p-2.5 text-lux-silver hover:bg-white/5 md:hidden" aria-label="Open menu" data-testid="public-header-menu-button">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[86vw] max-w-sm overflow-y-auto border-lux-border bg-lux-bg" data-testid="public-mobile-menu">
          <SheetHeader className="text-left">
            <SheetTitle><Wordmark size="text-2xl" to={null} /></SheetTitle>
            <SheetDescription className="text-lux-muted">Verified independent advertisers in London, Birmingham and Manchester.</SheetDescription>
          </SheetHeader>
          <nav className="mt-6 flex flex-col" aria-label="Mobile">
            <p className="lux-label">Cities</p>
            <div onClick={() => setOpen(false)} className="mt-1 flex flex-col">
              {cities.map((c) => (
                <Link key={c.slug} to={`/${c.slug}`} className="flex items-center justify-between border-b border-lux-border py-3.5 font-serif text-2xl text-lux-text hover:text-lux-silverBright" data-testid={`mobile-menu-city-${c.slug}`}>
                  {c.name} {c.is_default && <span className="text-[10px] uppercase tracking-luxLabel text-lux-silver">Main city</span>}
                </Link>
              ))}
              {[[`/${city?.slug || DEFAULT_CITY}/browse`, `Browse all in ${city?.name || "London"}`], ["/saved", `Saved profiles${saved.length ? ` (${saved.length})` : ""}`], ["/safety", "Safety"], ["/help", "Help & FAQ"]].map(([to, label]) => (
                <Link key={to} to={to} className="border-b border-lux-border py-3 text-base text-lux-text/90 hover:text-lux-silverBright">{label}</Link>
              ))}
            </div>
            <p className="lux-label mt-6">{city?.name} areas</p>
            <div className="mt-3 grid grid-cols-2 gap-2" onClick={() => setOpen(false)}>
              {areas.map((a) => (
                <Link key={a.slug} to={`/${city.slug}/${a.slug}`} className="lux-chip justify-center py-2" data-testid={`mobile-menu-area-${a.slug}`}>{a.name}</Link>
              ))}
            </div>
            <label className="mt-6 flex items-center justify-between rounded-[10px] border border-lux-border p-3.5 text-sm">
              <span className="flex items-center gap-2 text-lux-text/90">{blur ? <EyeOff className="h-4 w-4 text-lux-silver" /> : <Eye className="h-4 w-4 text-lux-silver" />} Blur photos while browsing</span>
              <Switch checked={blur} onCheckedChange={setBlur} data-testid="mobile-menu-blur-switch" aria-label="Blur photos while browsing" />
            </label>
            <div className="mt-6 flex flex-col gap-2" onClick={() => setOpen(false)}>
              <Link to="/advertiser/login" className="lux-btn lux-btn-primary" data-testid="mobile-menu-advertiser-login">For advertisers</Link>
              <Link to="/report" className="lux-btn lux-btn-ghost"><Flag className="h-4 w-4" /> Report a concern</Link>
            </div>
            <p className="mt-6 flex items-center gap-2 pb-6 text-xs text-lux-muted"><ShieldCheck className="h-4 w-4 text-lux-silver" /> 18+ only. Every profile is verified and moderated.</p>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
